import React, { useEffect, useState } from 'react';
import guestGuideApi from '../../services/guestGuideApi';
import useLanguage from '../../hooks/useLanguage';

/**
 * «قواعد الإقامة الخاصة بالوحدة» — a unit's optional replacement for the company house rules.
 * tasks/guest-guide-sections-and-print-sheet.md (Phase 2, G3).
 *
 * Shows the company rules read-only beside the override, so whoever edits can see exactly what
 * the guest gets: the unit's own list when it has one, otherwise the company list.
 *
 * Rules are stored as a list but edited one per line. Raw lines (blanks included) go to the
 * guidebook state so typing a newline is not swallowed mid-edit; the server drops blank lines and
 * caps the list at 15.
 *
 * @param {{rulesAr: string[], rulesEn: string[]}} value
 * @param {(field: 'rulesAr'|'rulesEn', lines: string[]) => void} onChange
 */
// Mirror the server's limits (be/routes/securePropertyRoutes.js sanitizeRuleList).
const MAX_RULES = 15;
const MAX_RULE_LENGTH = 300;

const UnitHouseRulesCard = ({ value, onChange }) => {
  const { t } = useLanguage();
  const [companyRules, setCompanyRules] = useState(null);

  useEffect(() => {
    guestGuideApi.getDefaults()
      .then((d) => setCompanyRules(d.houseRulesAr || []))
      .catch((error) => {
        // Reference only — the override still works without it. Left hidden (null) rather than
        // shown as "not written yet", which would be false for a role that simply cannot read it.
        console.error('Company house rules read failed:', error);
      });
  }, []);

  const rulesAr = value?.rulesAr || [];
  const rulesEn = value?.rulesEn || [];
  const usesOwn = rulesAr.some((r) => r.trim());

  // The server keeps the first 15 non-blank rules and cuts each at 300 characters; say so here
  // rather than let the extra lines vanish silently.
  const notice = (lines) => {
    const filled = lines.filter((r) => r.trim());
    if (filled.length > MAX_RULES) return t('guestGuide.unit.rulesOverLimit');
    if (filled.some((r) => r.trim().length > MAX_RULE_LENGTH)) return t('guestGuide.unit.ruleTooLong');
    return null;
  };
  const counter = (lines) => `${t('guestGuide.unit.rulesCount')}: ${lines.filter((r) => r.trim()).length} / ${MAX_RULES}`;

  return (
    <div className="card p-3 mb-4">
      <h5 className="mb-1">{t('guestGuide.unit.unitRules')}</h5>
      <p className="text-muted small mb-3">{t('guestGuide.unit.unitRulesHint')}</p>

      <div className="mb-3 p-2" style={{ background: '#f8f9fa', borderRadius: '6px' }}>
        <strong className="small d-block mb-1">
          {usesOwn ? t('guestGuide.unit.usingUnitRules') : t('guestGuide.unit.usingCompanyRules')}
        </strong>
        {companyRules !== null && <small className="text-muted d-block">{t('guestGuide.unit.companyRules')}:</small>}
        {companyRules === null ? null : companyRules.length ? (
          <ul className="small mb-0" style={{ paddingInlineStart: '18px' }}>
            {companyRules.map((rule) => <li key={rule}>{rule}</li>)}
          </ul>
        ) : (
          <small className="text-muted">{t('guestGuide.unit.companyRulesNone')}</small>
        )}
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label" htmlFor="unit-rules-ar">{t('guestGuide.unit.unitRulesAr')}</label>
          <textarea
            id="unit-rules-ar"
            className="form-control"
            rows="4"
            dir="rtl"
            style={{ textAlign: 'right' }}
            value={rulesAr.join('\n')}
            onChange={(e) => onChange('rulesAr', e.target.value.split('\n'))}
          />
          <small className="text-muted d-block mt-1">{counter(rulesAr)}</small>
          {notice(rulesAr) && <small className="text-danger d-block">{notice(rulesAr)}</small>}
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label d-block" htmlFor="unit-rules-en" dir="auto">{t('guestGuide.unit.unitRulesEn')}</label>
          <textarea
            id="unit-rules-en"
            className="form-control"
            rows="4"
            dir="ltr"
            style={{ textAlign: 'left' }}
            value={rulesEn.join('\n')}
            onChange={(e) => onChange('rulesEn', e.target.value.split('\n'))}
          />
          <small className="text-muted d-block mt-1" dir="auto">{counter(rulesEn)}</small>
          {notice(rulesEn) && <small className="text-danger d-block" dir="auto">{notice(rulesEn)}</small>}
        </div>
      </div>
    </div>
  );
};

export default UnitHouseRulesCard;
